import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useAuth from '@hooks/useAuth';
import { getTeams, getTeamStripeId } from '@supa/teams';
import { getTeamMembers } from '@supa/users';
import { stringFromError } from '@utils/utils';
import { log, logError } from '@utils/logging';
import { TeamAthlete, COACH_ROLE, TEAM_ROLE, AthleteTeamMember, STATUS } from '@type/db-types';
import { TeamContext, TeamContextValues } from '@hooks/useTeams';
import useWatchStripeAccount from '@hooks/useWatchStripeAccount';

export default function TeamProvider({
    children,
}: {
    children: React.JSX.Element;
}): React.JSX.Element {
    const [teams, setTeams] = useState<TeamAthlete[] | undefined>();
    const [selectedTeam, setSelectedTeam] = useState<TeamAthlete | undefined>();
    const [teamMembers, setTeamMembers] = useState<TeamAthlete[] | undefined>(undefined);
    const [athletes, setAthletes] = useState<AthleteTeamMember[] | undefined>(undefined);
    const [coaches, setCoaches] = useState<TeamAthlete[] | undefined>(undefined);
    const [parents, setParents] = useState<TeamAthlete[] | undefined>(undefined);
    const { authUser } = useAuth();
    const userMembershipInfo = useMemo(
        () => teamMembers?.find(({ userId }) => userId === authUser?.id),
        [authUser?.id, teamMembers],
    );
    const role: TeamContextValues['role'] = userMembershipInfo?.role as TEAM_ROLE;
    const [stripePaymentUser, setStripePaymentUser] = useState<{
        stripeId: string;
        userId: string;
    } | null>(null);
    const stripeState = useWatchStripeAccount(
        stripePaymentUser?.userId,
        `TeamProvider  ${selectedTeam?.id}`,
    );

    const refreshTeams = useCallback(
        () =>
            getTeams()
                .then((updatedTeams) => {
                    const teams: TeamAthlete[] = updatedTeams.map((team) => ({
                        ...team,
                        createdAt: team.teamCreatedAt,
                        id: team.teamId,
                        updatedAt: team.teamUpdatedAt,
                        userStatus: STATUS.ACTIVE,
                    }));
                    setTeams(teams);

                    // auto-select the team if there is only one
                    if (updatedTeams.length === 1) {
                        setSelectedTeam(teams[0]);
                    }
                })
                .catch((e) => {
                    log(`[refreshTeams] ${stringFromError(e)}`);
                }),
        [],
    );

    const refreshMembers = useCallback(() => {
        return getTeamMembers(selectedTeam?.teamId)
            .then((members) => {
                const parsedAthletes: AthleteTeamMember[] =
                    members
                        // The issue here is the "Athlete" type doesn't include "membership" info, which we are using all over
                        // gonna need a hybrid type that extends "Athlete" and includes "membership" info
                        .filter(({ role }) => role === TEAM_ROLE.ATHLETE)
                        .map((m) => ({
                            ...m,
                            id: m.userId,
                            activeSince: m.activeSince as string, // user is athlete
                            birthday: m.birthday as string, // user is athlete
                            createdAt: m.athleteCreatedAt as string, // user is athlete
                            gender: m.gender as string, // user is athlete
                            grade: m.grade as string, // user is athlete
                            imageUrl: m.userImageUrl,
                            status: m.userStatus,
                            updatedAt: m.athleteUpdatedAt as string, // user is athlete
                            weight: m.weight as number, // user is athlete
                        })) || [];
                setAthletes(parsedAthletes);
                setCoaches(members.filter(({ role }) => COACH_ROLE.includes(role)));
                setParents(members.filter(({ role }) => role === TEAM_ROLE.PARENT));
                setTeamMembers(members);
            })
            .catch((e) => {
                log(`[refreshMembers] ${stringFromError(e)}`);
                setAthletes((prev) => prev || []);
                setCoaches((prev) => prev || []);
                setParents((prev) => prev || []);
                setTeamMembers([]);
            });
    }, [selectedTeam?.teamId]);

    useEffect(() => {
        // fetch the user's teams
        if (authUser?.id) refreshTeams();
    }, [authUser?.id, refreshTeams]);

    useEffect(() => {
        if (selectedTeam?.id) {
            console.log('refreshing members');
            refreshMembers();
            getTeamStripeId(selectedTeam.id).then(setStripePaymentUser).catch(logError);
        }
    }, [refreshMembers, selectedTeam?.id]);

    const teamContext: TeamContextValues = useMemo(() => {
        return {
            athletes,
            coaches,
            loading:
                teams === undefined ||
                !teamMembers?.[0] ||
                teamMembers?.[0].teamId !== selectedTeam?.teamId,
            parents,
            refreshTeams,
            refreshMembers,
            role,
            selectedTeam,
            setSelectedTeam,
            stripePaymentUser,
            stripeState,
            teams,
            teamMembers,
            userMembershipInfo,
        };
    }, [
        athletes,
        coaches,
        parents,
        refreshMembers,
        refreshTeams,
        role,
        selectedTeam,
        stripePaymentUser,
        stripeState,
        teams,
        teamMembers,
        userMembershipInfo,
    ]);

    return <TeamContext.Provider value={teamContext}>{children}</TeamContext.Provider>;
}
